import { useRef, type RefObject } from "react";

import { CaptureResetWarningOverlay } from "#/components/game/CaptureResetWarningOverlay";
import { DamageForecast } from "#/components/game/DamageForecast";
import { GameCanvas } from "#/components/game/GameCanvas";
import type { GameCanvasHandle, GameCanvasViewState } from "#/components/game/gameCanvas/types";
import { GameDockedPanelShell } from "#/components/game/GameDockedPanelShell";
import { OpponentTurnBanner } from "#/components/game/OpponentTurnBanner";
import { ProductionBuildMenu } from "#/components/game/ProductionBuildMenu";
import { PlayMapHudBar } from "#/components/play/PlayMapPageShell/hudCanvas";
import {
  useAiPlaybackStepTimer,
  useCancelGameplaySelection,
  usePlayMapBoardChrome,
} from "#/components/play/usePlayMapSession";
import {
  useAttackableTileKeys,
  useAttackableUnitIds,
  useAttackRangeFlashStyle,
  useAttackRangeFlashTileKeys,
  useBoardCursor,
  useCanvasHoverSync,
  useCaptureBarHoverPreview,
  useHiddenCaptureBarTileKeys,
  usePathKeys,
  usePendingAttack,
  usePreviewAnimation,
  usePreviewUnit,
  useReachableKeys,
  useSelectedUnitId,
  useUnmovedHighlightFadeStartedAtMs,
  useUnmovedHighlightUnitIds,
} from "#/game/gameSelectors";
import { useGameSelector } from "#/game/GameStoreProvider";

import { TutorialBottomDock } from "./TutorialBottomDock";
import { TutorialCoach } from "./TutorialCoach";
import { useMarkTutorialCompletedOnFinish } from "./useMarkTutorialCompletedOnFinish";
import { useTutorialController, type TutorialController } from "./useTutorialController";

function useTutorialViewState(): GameCanvasViewState {
  return {
    selectedUnitId: useSelectedUnitId(),
    reachableKeys: useReachableKeys(),
    pathKeys: usePathKeys(),
    previewUnit: usePreviewUnit(),
    previewAnimation: usePreviewAnimation(),
    attackableUnitIds: useAttackableUnitIds(),
    attackableTileKeys: useAttackableTileKeys(),
    attackRangeFlashTileKeys: useAttackRangeFlashTileKeys(),
    attackRangeFlashStyle: useAttackRangeFlashStyle(),
    unmovedHighlightUnitIds: useUnmovedHighlightUnitIds(),
    unmovedHighlightFadeStartedAtMs: useUnmovedHighlightFadeStartedAtMs(),
    hiddenCaptureBarTileKeys: useHiddenCaptureBarTileKeys(),
    captureBarHoverPreview: useCaptureBarHoverPreview(),
  };
}

function TutorialBuildMenu({
  controller,
  bottomDockRef,
}: {
  controller: TutorialController;
  bottomDockRef: RefObject<HTMLElement | null>;
}) {
  const selection = useGameSelector((s) => s.selection);
  const localState = useGameSelector((s) => s.localState);
  const cancelSelection = useGameSelector((s) => s.cancelSelection);
  if (selection.mode !== "building") return null;

  const owner = "p1";

  const options = selection.options.map((option) =>
    controller.buildKind && option.kind !== controller.buildKind
      ? { ...option, disabled: true, disabledReason: null }
      : option,
  );

  return (
    <GameDockedPanelShell
      bottomDockRef={bottomDockRef}
      onCancel={cancelSelection}
      closeAriaLabel="Close build menu"
    >
      <ProductionBuildMenu
        buildingKind={selection.buildingKind}
        capacity={selection.capacity}
        funds={localState.funds[owner]}
        options={options}
        owner={owner}
        onChoose={(kind) => controller.chooseBuild(selection.pos, kind)}
        onCancel={cancelSelection}
      />
    </GameDockedPanelShell>
  );
}

function TutorialAttackForecast({
  bottomDockRef,
}: {
  bottomDockRef: RefObject<HTMLElement | null>;
}) {
  const pendingAttack = usePendingAttack();
  const onConfirmAttack = useGameSelector((s) => s.confirmAttack);
  const onCancelAttack = useGameSelector((s) => s.cancelAttack);
  if (!pendingAttack) return null;

  return (
    <GameDockedPanelShell
      bottomDockRef={bottomDockRef}
      onCancel={onCancelAttack}
      closeAriaLabel="Cancel attack"
      panelWidth="fit"
    >
      <DamageForecast
        attackerKind={pendingAttack.attackerKind}
        attackerOwner={pendingAttack.attackerOwner}
        targetKind={pendingAttack.targetKind}
        targetOwner={pendingAttack.targetOwner}
        forecast={pendingAttack.forecast}
        counterForecast={pendingAttack.counterForecast}
        willKillTarget={pendingAttack.willKillTarget}
        willKillAttacker={pendingAttack.willKillAttacker}
        onConfirm={onConfirmAttack}
        onCancel={onCancelAttack}
      />
    </GameDockedPanelShell>
  );
}

function TutorialViewportOverlay({
  controller,
  bottomDockRef,
  onRestart,
}: {
  controller: TutorialController;
  bottomDockRef: RefObject<HTMLElement | null>;
  onRestart: () => void;
}) {
  const selectionMode = useGameSelector((s) => s.selection.mode);
  const hideCoach =
    selectionMode === "building" ||
    selectionMode === "confirm-attack" ||
    selectionMode === "confirm-capture-reset";

  return (
    <>
      <TutorialBuildMenu controller={controller} bottomDockRef={bottomDockRef} />
      <TutorialAttackForecast bottomDockRef={bottomDockRef} />
      <CaptureResetWarningOverlay bottomDockRef={bottomDockRef} />
      {!hideCoach || controller.blocksInteraction ? (
        <TutorialCoach
          prompt={controller.prompt}
          isFinal={controller.isFinal}
          bottomDockRef={bottomDockRef}
          onRestart={onRestart}
          onDismiss={controller.dismissCoach}
        />
      ) : null}
    </>
  );
}

function useTutorialCanvasBindings() {
  const localState = useGameSelector((s) => s.localState);
  const animation = useGameSelector((s) => s.animation);
  const playbackActive = useGameSelector((s) => Boolean(s.aiPlayback));
  const onTileHover = useGameSelector((s) => s.setHoveredTile);
  const onCancelSelection = useCancelGameplaySelection();
  const onAnimationComplete = useGameSelector((s) => s.completeAnimation);
  const cursorMode = useBoardCursor();
  const viewState = useTutorialViewState();
  const canvasHandleRef = useRef<GameCanvasHandle | null>(null);
  useCanvasHoverSync(canvasHandleRef);
  return {
    localState,
    animation,
    playbackActive,
    onTileHover,
    onCancelSelection,
    onAnimationComplete,
    cursorMode,
    viewState,
    canvasHandleRef,
  };
}

function TutorialCanvas({
  controller,
  bottomDockRef,
  onRestart,
  scrollFocus,
  scrollChrome,
}: {
  controller: TutorialController;
  bottomDockRef: RefObject<HTMLDivElement | null>;
  onRestart: () => void;
} & Pick<Parameters<typeof GameCanvas>[0], "scrollFocus" | "scrollChrome">) {
  const b = useTutorialCanvasBindings();
  const showOpponentTurn = b.playbackActive && b.localState.status === "in_progress";

  return (
    <div className="relative w-full">
      {showOpponentTurn ? <OpponentTurnBanner /> : null}
      <div className="flex w-full justify-center">
        <div className="relative w-max max-w-full">
          <GameCanvas
            ref={b.canvasHandleRef}
            state={b.localState}
            viewState={b.viewState}
            animation={b.animation}
            cursorMode={b.cursorMode}
            onTileSelect={controller.selectTile}
            onTileHover={b.onTileHover}
            onCancelSelection={b.onCancelSelection}
            onAnimationComplete={b.onAnimationComplete}
            scrollFocus={scrollFocus}
            scrollChrome={scrollChrome}
            viewportOverlay={
              <TutorialViewportOverlay
                controller={controller}
                bottomDockRef={bottomDockRef}
                onRestart={onRestart}
              />
            }
          />
        </div>
      </div>
    </div>
  );
}

export function TutorialBoard({ onRestart }: { onRestart: () => void }) {
  useAiPlaybackStepTimer();
  const controller = useTutorialController();
  useMarkTutorialCompletedOnFinish(controller.isFinal);
  const { boardScrollFocus, topDockRef, bottomDockRef, scrollChrome } = usePlayMapBoardChrome();

  return (
    <main className="flex w-full min-w-0 grow flex-col items-center bg-background">
      <div className="relative flex w-max max-w-full flex-col sm:min-w-[576px]">
        <div
          ref={topDockRef}
          className="sticky top-[max(0px,env(safe-area-inset-top))] z-30 shrink-0"
        >
          <div className="border-b border-border bg-card shadow-[0_4px_24px_rgba(0,0,0,0.08)]">
            <PlayMapHudBar />
          </div>
        </div>

        <TutorialCanvas
          controller={controller}
          bottomDockRef={bottomDockRef}
          onRestart={onRestart}
          scrollFocus={boardScrollFocus}
          scrollChrome={scrollChrome}
        />

        <TutorialBottomDock
          bottomDockRef={bottomDockRef}
          controller={controller}
          onRestart={onRestart}
        />
      </div>
    </main>
  );
}
